import { useApplicantAPI } from "@certn/api";
import { Container, Loader, SectionMessage } from "@certn/cascadia-react";

import { getRegionKey } from "@certn/utils";
import { css } from "@emotion/css";
import { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { DashboardCards } from "./DashboardCards";
import { DashboardEmpty } from "./DashboardEmpty";
import { HistoryCards } from "./HistoryCards";
import { MonitoringClientsSection } from "../monitoring/MonitoringClientsSection";
import { SubmitCheckModal } from "./SubmitCheckModal";
import { useDocumentTitle } from "../../hooks/api/useDocumentTitle";
import { useLanguage } from "../../hooks/useLanguage";
import { useNetworkState } from "../../hooks/useNetworkState";
import { DismissCancelModal } from "./DismissCancelModal";
import { isCancelledCaseDismissed } from "../../utils";

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  useDocumentTitle(t("Dashboard — Certn"));
  const { data: openCases, isLoading: openCasesLoading } = useApplicantAPI(
    "/api/internal/applicant/case/open/"
  );
  const { data: completeCases, isLoading: completeCasesLoading } =
    useApplicantAPI("/api/internal/applicant/case/complete/");

  const location = useLocation();
  const [showSuccessModal, setShowSuccessModal] = useState(
    location.state?.showSuccessModal ?? false
  );
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const { isOnline } = useNetworkState();
  const [cancelledCaseId, setCancelledCaseId] = useState<string>("");
  const { changeLanguage } = useLanguage();

  const auRegionWrongLanguage =
    getRegionKey() === "au" && i18n.language !== "en-AU";

  if (auRegionWrongLanguage) {
    changeLanguage("en-AU");
  }

  useEffect(() => {
    const cancelledCase = completeCases?.find(
      (c) =>
        c.overall_status === "CANCELLED" &&
        c.short_id &&
        !isCancelledCaseDismissed(c.short_id)
    );
    if (cancelledCase) {
      setShowCancellationModal(true);
      setCancelledCaseId(cancelledCase.short_id ?? "");
    }
  }, [completeCases]);

  if (openCasesLoading || completeCasesLoading) {
    return <Loader type="spinner" size="default" overlay />;
  }

  if (!openCases?.length && !completeCases?.length && !showSuccessModal) {
    return <DashboardEmpty />;
  }

  return (
    <Container className={css({ rowGap: "var(--space-16)" })}>
      {!isOnline && (
        <SectionMessage
          sectionTitle={t("Oops! You're currently offline")}
          type="danger"
          icon="error"
        >
          <Trans>Please check your internet connection</Trans>
        </SectionMessage>
      )}
      <DashboardCards />
      <MonitoringClientsSection />
      <HistoryCards />
      <SubmitCheckModal
        show={showSuccessModal}
        provideFeedback={false}
        setShowSuccessModal={setShowSuccessModal}
      />
      <DismissCancelModal
        showCancellationModal={showCancellationModal && !!cancelledCaseId}
        setShowCancellationModal={setShowCancellationModal}
        caseId={cancelledCaseId}
      />
    </Container>
  );
}
